import React from "react";
import { C } from "../constants/baziData";

// ── PersonalizedInsightPanel ─────────────────────────────────────────────────
function PersonalizedInsightPanel({ data, lang, user, setPage }) {
  const [open, setOpen] = React.useState(true);
  const zh = lang === "zh";

  // Phase 9: tier gate — L1+ sees real content; L0 / guest sees BlurGate teaser
  const isPaid = user && (user.tier === "pro" || user.tier === "L1" || user.tier === "L2" || user.tier === "L3" || user.tier === "L4");

  // If guest or L0 with no backend data → show BlurGate teaser card
  if (!isPaid || !data || !data.report) {
    return (
      <div style={{ position:"relative", borderRadius:14, overflow:"hidden",
        border:"1px solid rgba(212,175,55,.22)", marginTop:4 }}>
        {/* blurred preview behind gate */}
        <div style={{ filter:"blur(5px)", pointerEvents:"none", userSelect:"none",
          background:"rgba(255,255,255,.72)", backdropFilter:"blur(16px)",
          WebkitBackdropFilter:"blur(16px)", padding:"24px 28px" }}>
          <div style={{ fontSize:11, letterSpacing:".2em", color:"rgba(212,175,55,.55)",
            textTransform:"uppercase", marginBottom:8 }}>
            {zh ? "个性化洞察" : "Personalized Insight"}
          </div>
          <div style={{ height:12, borderRadius:6, background:"rgba(212,175,55,.18)", marginBottom:10, width:"60%" }}/>
          <div style={{ height:10, borderRadius:6, background:"rgba(212,175,55,.10)", marginBottom:8, width:"90%" }}/>
          <div style={{ height:10, borderRadius:6, background:"rgba(212,175,55,.10)", marginBottom:8, width:"80%" }}/>
          <div style={{ height:10, borderRadius:6, background:"rgba(212,175,55,.10)", marginBottom:8, width:"70%" }}/>
          <div style={{ height:10, borderRadius:6, background:"rgba(212,175,55,.08)", marginBottom:8, width:"85%" }}/>
          <div style={{ height:10, borderRadius:6, background:"rgba(212,175,55,.08)", width:"65%" }}/>
        </div>
        {/* overlay gate */}
        <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"center", gap:12,
          background:"rgba(249,247,242,.55)", backdropFilter:"blur(2px)" }}>
          <div style={{ fontSize:18, color: C.gold, letterSpacing:".05em",
            fontFamily:"'Cormorant Garamond',serif", fontWeight:600, textAlign:"center" }}>
            {zh ? "☸ 深度个性化洞察" : "☸ Deep Personalised Insight"}
          </div>
          <div style={{ fontSize:12.5, color: C.mist2, textAlign:"center",
            maxWidth:280, lineHeight:1.7 }}>
            {zh
              ? "根据您的命盘生成专属的职业/家庭/修行模版分析，含官星受制检测与当月能量叠加解读。"
              : "Your chart generates a tailored Career / Family / Practitioner template analysis, including authority-suppression detection and current-month energy overlay."}
          </div>
          <button
            onClick={() => setPage ? setPage("pricing") : null}
            style={{
              marginTop:4,
              background:"linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)",
              color:"#1a0a0a", border:"none", borderRadius:9999,
              padding:"11px 32px", fontSize:13, fontWeight:700,
              letterSpacing:".08em", cursor:"pointer",
              boxShadow:"0 4px 18px rgba(212,175,55,.35)",
              transition:"transform .15s, box-shadow .15s",
            }}
            onMouseEnter={e => { e.target.style.transform="scale(1.04)"; e.target.style.boxShadow="0 6px 24px rgba(212,175,55,.5)"; }}
            onMouseLeave={e => { e.target.style.transform="scale(1)";    e.target.style.boxShadow="0 4px 18px rgba(212,175,55,.35)"; }}
          >
            {zh ? "升级解锁 · L1 起" : "Unlock with L1 ↑"}
          </button>
          <div style={{ fontSize:11, color: C.mist4, letterSpacing:".05em" }}>
            {zh ? "从 $99 / 月起 · 立即激活" : "From $99 / mo · Activate instantly"}
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.report) return null;

  const { template_detected, report } = data;

  const TEMPLATE_LABELS = {
    career_architect:       { zh: "职业建筑师", en: "Career Architect",       icon: "⚙" },
    family_guardian:        { zh: "家族守护者", en: "Family Guardian",         icon: "🏡" },
    aspiration_practitioner:{ zh: "愿力行者",   en: "Aspiration Practitioner", icon: "☸" },
  };
  const tLabel = TEMPLATE_LABELS[template_detected] || TEMPLATE_LABELS.career_architect;

  const SEV_COLORS = {
    extreme: { bg: "rgba(220,38,38,.06)", border: "rgba(220,38,38,.22)", text: "#DC2626", badge: "⚡" },
    high:    { bg: "rgba(212,175,55,.07)", border: "rgba(212,175,55,.28)", text: "#D4AF37", badge: "⚠" },
    warning: { bg: "rgba(212,175,55,.05)", border: "rgba(212,175,55,.20)", text: "#B8860B", badge: "◈" },
    normal:  { bg: "rgba(255,255,255,.55)", border: "rgba(212,175,55,.14)", text: C.mist2, badge: "◎" },
    harmony: { bg: "rgba(209,250,229,.50)", border: "rgba(6,78,59,.18)",    text: C.emerald, badge: "✦" },
    deep_protection: { bg: "rgba(212,175,55,.07)", border: "rgba(212,175,55,.28)", text: "#D4AF37", badge: "🛡" },
  };

  // Fine gold divider
  const GoldLine = () => (
    <div style={{ width:"100%", height:1,
      background:"linear-gradient(90deg,transparent,rgba(212,175,55,.28),transparent)",
      margin:"14px 0" }} />
  );

  // Single insight card
  const InsightCard = ({ insight }) => {
    const sev = SEV_COLORS[insight.severity] || SEV_COLORS.normal;
    return (
      <div style={{ borderRadius:8, padding:"12px 16px", marginBottom:8,
        background: sev.bg, border:`1px solid ${sev.border}` }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:".06em",
          color: sev.text, marginBottom:5 }}>
          {sev.badge}&nbsp;{insight.label}
        </div>
        <div style={{ fontSize:13, lineHeight:1.8, color: C.mist2 }}>
          {insight.body}
        </div>
      </div>
    );
  };

  // ── Career Architect ──────────────────────────────────────────────────────
  const CareerView = () => (
    <>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12 }}>
        {Object.entries(report.ten_god_profile||{})
          .sort((a,b) => b[1]-a[1])
          .map(([god, cnt]) => cnt > 0 && (
            <div key={god} style={{
              fontSize:11, padding:"3px 10px", borderRadius:9999,
              background: god === report.dominant_ten_god
                ? "rgba(212,175,55,.15)" : "rgba(255,255,255,.55)",
              border: god === report.dominant_ten_god
                ? "1px solid rgba(212,175,55,.40)" : "1px solid rgba(212,175,55,.14)",
              color: god === report.dominant_ten_god ? C.gold : C.mist3,
              fontWeight: god === report.dominant_ten_god ? 700 : 400,
              letterSpacing:".04em",
            }}>
              {god} ×{cnt}
            </div>
          ))
        }
      </div>

      {report.decision_fatigue && (
        <div style={{ background:"rgba(220,38,38,.05)", border:"1px solid rgba(220,38,38,.20)",
          borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#DC2626", marginBottom:4, letterSpacing:".05em" }}>
            ⚡ {zh ? "决策磨损预警" : "Decision Fatigue Alert"}
          </div>
          <div style={{ fontSize:12.5, color:"rgba(180,30,30,.85)", lineHeight:1.75 }}>
            {zh
              ? `官杀节点 ${report.ten_god_profile?.["官杀"]||0} 处，决策频道长期超负荷。本月聚焦单一高杠杆事项，委托其余。`
              : `${report.ten_god_profile?.["官杀"]||0} official-pressure nodes. Decision bandwidth is overloaded. Focus on one high-leverage item — delegate the rest.`}
          </div>
        </div>
      )}

      {(report.insights||[]).filter(i => i.type !== "decision_fatigue").map((ins, i) => (
        <InsightCard key={i} insight={ins} />
      ))}

      {report.monthly_element_advice && (
        <>
          <GoldLine />
          <div style={{ fontSize:13, color: C.mist3, lineHeight:1.8, fontStyle:"italic",
            fontFamily:"'Cormorant Garamond',serif" }}>
            {report.monthly_element_advice}
          </div>
        </>
      )}

      {report.alert_overlay && report.alert_overlay.label && (
        <>
          <GoldLine />
          <InsightCard insight={{ label: report.alert_overlay.label, body: report.alert_overlay.body, severity: report.alert_overlay.severity }} />
        </>
      )}

      {report.age_context && (
        <>
          <GoldLine />
          <div style={{ fontSize:12.5, color: C.mist3, lineHeight:1.85,
            borderLeft:`2px solid rgba(212,175,55,.25)`, paddingLeft:12 }}>
            🕐&nbsp;{report.age_context}
          </div>
        </>
      )}

      {report.gender_note && (
        <div style={{ marginTop:8, fontSize:12, color:"rgba(180,130,20,.75)",
          lineHeight:1.8, fontStyle:"italic" }}>
          ◈&nbsp;{report.gender_note}
        </div>
      )}

      {report.guisi_career_overlay && (
        <>
          <GoldLine />
          <div style={{ fontSize:10.5, letterSpacing:".12em", color:"rgba(212,175,55,.55)",
            textTransform:"uppercase", marginBottom:8 }}>
            {zh ? "癸巳月专项" : "Guisi Month Overlay"}
          </div>
          <div style={{ background:"rgba(212,175,55,.05)", border:"1px solid rgba(212,175,55,.18)",
            borderRadius:8, padding:"12px 14px", marginBottom:8 }}>
            <div style={{ fontSize:11, fontWeight:700, color: C.gold, marginBottom:5 }}>
              {zh ? "⧖ 逻辑闭环协议" : "⧖ Logic Closure Protocol"}
            </div>
            <div style={{ fontSize:12.5, color: C.mist2, lineHeight:1.85, whiteSpace:"pre-line" }}>
              {report.guisi_career_overlay.decision_closure}
            </div>
          </div>
          <div style={{ background:"rgba(212,175,55,.04)", border:"1px solid rgba(212,175,55,.14)",
            borderRadius:8, padding:"12px 14px" }}>
            <div style={{ fontSize:11, fontWeight:700, color: C.gold, marginBottom:5 }}>
              {zh ? "⇄ 系统能量切换" : "⇄ System Transition"}
            </div>
            <div style={{ fontSize:12.5, color: C.mist3, lineHeight:1.85 }}>
              {report.guisi_career_overlay.system_transition}
            </div>
          </div>
        </>
      )}
    </>
  );

  // ── Family Guardian ───────────────────────────────────────────────────────
  const FamilyView = () => (
    <>
      {(report.family_conflicts||[]).length === 0 ? (
        <div style={{ fontSize:13, color: C.mist3, padding:"8px 0" }}>
          {zh ? "未检测到与2008/2013/2015生人的地支冲刑。" : "No branch conflicts detected with 2008/2013/2015 birth years."}
        </div>
      ) : (report.family_conflicts||[]).map((fc, i) => {
        const sev = SEV_COLORS[fc.severity] || SEV_COLORS.normal;
        return (
          <div key={i} style={{ borderRadius:8, padding:"12px 16px", marginBottom:8,
            background: sev.bg, border:`1px solid ${sev.border}` }}>
            <div style={{ fontSize:11, fontWeight:700, color: sev.text, marginBottom:4, letterSpacing:".05em" }}>
              {sev.badge}&nbsp;{fc.child_label}&nbsp;×&nbsp;{zh ? "命局" : "Chart"} {fc.natal_pillar}
            </div>
            <div style={{ fontSize:13, color: C.gold, marginBottom:4, fontWeight:600 }}>
              {fc.relation_label}
            </div>
            <div style={{ fontSize:12.5, color: C.mist2, lineHeight:1.75 }}>
              {fc.remedy}
            </div>
          </div>
        );
      })}

      {report.wei_xu_xing_detected && (
        <>
          <GoldLine />
          <div style={{ background:"rgba(212,175,55,.07)", border:"1px solid rgba(212,175,55,.25)",
            borderRadius:8, padding:"10px 14px" }}>
            <div style={{ fontSize:11, fontWeight:700, color: C.gold, marginBottom:4 }}>
              〰 {zh ? "未戌相刑 — 厚土格局" : "Wei-Xu Penalty — Heavy Earth Pattern"}
            </div>
            <div style={{ fontSize:12.5, color: C.mist2, lineHeight:1.75 }}>
              {zh
                ? "命局存在未戌相刑，土气积聚易引发内部阻滞。家庭中易出现权力边界不清或情绪积压。建议以结构化沟通化解。"
                : "Wei-Xu penalty detected. Earth accumulates — watch for blurry authority boundaries or emotional buildup. Structured communication is key."}
            </div>
          </div>
        </>
      )}

      {report.age_context && (
        <>
          <GoldLine />
          <div style={{ fontSize:12.5, color: C.mist3, lineHeight:1.85,
            borderLeft:`2px solid rgba(212,175,55,.25)`, paddingLeft:12 }}>
            🕐&nbsp;{report.age_context}
          </div>
        </>
      )}

      {(report.month_child_interactions||[]).length > 0 && (
        <>
          <GoldLine />
          <div style={{ fontSize:10.5, letterSpacing:".12em", color:"rgba(212,175,55,.55)",
            textTransform:"uppercase", marginBottom:8 }}>
            {zh ? "癸巳月 × 孩子能量交互" : "Guisi Month × Child Interactions"}
          </div>
          {report.month_child_interactions.map((mc, i) => {
            const sevColor = mc.severity === "high" ? "rgba(220,38,38,.06)" :
                             mc.severity === "watch" ? "rgba(212,175,55,.07)" :
                             "rgba(209,250,229,.40)";
            const sevBorder = mc.severity === "high" ? "rgba(220,38,38,.22)" :
                              mc.severity === "watch" ? "rgba(212,175,55,.28)" :
                              "rgba(6,78,59,.18)";
            const sevText = mc.severity === "high" ? "#DC2626" :
                            mc.severity === "watch" ? C.gold : C.emerald;
            const badge = mc.severity === "high" ? "⚠" :
                          mc.severity === "watch" ? "〰" : "✦";
            return (
              <div key={i} style={{ borderRadius:8, padding:"12px 14px", marginBottom:8,
                background: sevColor, border:`1px solid ${sevBorder}` }}>
                <div style={{ fontSize:11, fontWeight:700, color: sevText, marginBottom:4 }}>
                  {badge}&nbsp;{mc.birth_year} {zh ? "年生" : "born"}
                </div>
                <div style={{ fontSize:12.5, color: C.mist2, lineHeight:1.8, marginBottom:6 }}>
                  {mc.interaction}
                </div>
                <div style={{ fontSize:12, color: C.mist3, lineHeight:1.75, marginBottom:4 }}>
                  {mc.remedy}
                </div>
                <div style={{ fontSize:11, color:"rgba(212,175,55,.6)", fontStyle:"italic" }}>
                  📍&nbsp;{mc.direction}
                </div>
              </div>
            );
          })}
        </>
      )}

      {report.family_boundary && (
        <>
          <GoldLine />
          <div style={{ background:"rgba(212,175,55,.04)", border:"1px solid rgba(212,175,55,.18)",
            borderRadius:8, padding:"12px 14px" }}>
            <div style={{ fontSize:11, fontWeight:700, color: C.gold, marginBottom:6, letterSpacing:".05em" }}>
              🏯&nbsp;{zh ? "家庭能量结界" : "Family Energy Field"}
            </div>
            <div style={{ fontSize:12, color: C.mist3, lineHeight:1.9, whiteSpace:"pre-line" }}>
              {report.family_boundary}
            </div>
          </div>
        </>
      )}
    </>
  );

  // ── Aspiration Practitioner ───────────────────────────────────────────────
  const AspirationView = () => (
    <>
      <div style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:11, letterSpacing:".14em", color:"rgba(212,175,55,.6)",
            textTransform:"uppercase", marginBottom:4 }}>
            {zh ? "本月本尊" : "This Month's Deity"}
          </div>
          <div style={{ fontSize:18, color: C.gold, fontWeight:600, fontFamily:"'Cormorant Garamond',serif" }}>
            {report.deity}
          </div>
          {report.mantra && (
            <div style={{ fontSize:11.5, color: C.mist3, marginTop:4, fontStyle:"italic",
              fontFamily:"'Cormorant Garamond',serif", lineHeight:1.7 }}>
              {report.mantra.length > 60 ? report.mantra.slice(0,60)+"…" : report.mantra}
            </div>
          )}
        </div>
        <div style={{ width:48, height:48, borderRadius:"50%",
          background:"rgba(212,175,55,.10)", border:"1px solid rgba(212,175,55,.28)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:20, flexShrink:0 }}>☸</div>
      </div>

      <GoldLine />

      {report.teaching && (
        <div style={{ fontSize:13.5, lineHeight:1.95, color: C.mist2,
          fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
          borderLeft:`2px solid rgba(212,175,55,.30)`, paddingLeft:14, marginBottom:12 }}>
          {report.teaching}
        </div>
      )}

      {report.mandala_visualization && (
        <div style={{ background:"rgba(212,175,55,.05)", border:"1px solid rgba(212,175,55,.18)",
          borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
          <div style={{ fontSize:10.5, letterSpacing:".12em", color:"rgba(212,175,55,.5)",
            textTransform:"uppercase", marginBottom:5 }}>
            {zh ? "坛城观修" : "Mandala Visualization"}
          </div>
          <div style={{ fontSize:12.5, color: C.mist3, lineHeight:1.8 }}>
            {report.mandala_visualization}
          </div>
        </div>
      )}

      {report.flow_relation_commentary && (
        <>
          <GoldLine />
          <div style={{ fontSize:12.5, color:"rgba(180,130,20,.8)", lineHeight:1.8 }}>
            ◈&nbsp;{report.flow_relation_commentary}
          </div>
        </>
      )}

      {report.alert_practice && (
        <>
          <GoldLine />
          <div style={{ background:"rgba(220,38,38,.05)", border:"1px solid rgba(220,38,38,.20)",
            borderRadius:8, padding:"10px 14px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#DC2626", marginBottom:4 }}>
              {report.alert_practice.title}
            </div>
            <div style={{ fontSize:12.5, color:"rgba(180,30,30,.85)", lineHeight:1.75, marginBottom:6 }}>
              {report.alert_practice.body}
            </div>
            {report.alert_practice.frequency && (
              <div style={{ fontSize:11, color:"rgba(212,175,55,.7)", fontStyle:"italic" }}>
                {report.alert_practice.frequency}
              </div>
            )}
          </div>
        </>
      )}

      {report.guisi_month_note && (
        <>
          <GoldLine />
          <div style={{ background:"rgba(212,175,55,.06)", border:"1px solid rgba(212,175,55,.22)",
            borderRadius:8, padding:"10px 14px" }}>
            <div style={{ fontSize:11, fontWeight:700, color: C.gold, marginBottom:4, letterSpacing:".05em" }}>
              〰&nbsp;{report.guisi_month_note.label}
            </div>
            <div style={{ fontSize:12.5, color: C.mist2, lineHeight:1.8, marginBottom: report.guisi_month_note.purification_guide ? 10 : 0 }}>
              {report.guisi_month_note.body}
            </div>
            {report.guisi_month_note.purification_guide && (
              <div style={{ borderTop:"1px solid rgba(212,175,55,.18)", paddingTop:10,
                fontSize:12, color: C.mist3, lineHeight:1.95, whiteSpace:"pre-line",
                fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic" }}>
                {report.guisi_month_note.purification_guide}
              </div>
            )}
          </div>
        </>
      )}

      {report.age_context && (
        <>
          <GoldLine />
          <div style={{ fontSize:12.5, color: C.mist3, lineHeight:1.85,
            borderLeft:`2px solid rgba(212,175,55,.25)`, paddingLeft:12 }}>
            🕐&nbsp;{report.age_context}
          </div>
        </>
      )}
    </>
  );

  const VIEWS = {
    career_architect: CareerView,
    family_guardian: FamilyView,
    aspiration_practitioner: AspirationView,
  };
  const ActiveView = VIEWS[template_detected] || CareerView;

  // Practices (shared footer)
  const practices = report.practices || [];

  return (
    <div style={{ background:"rgba(255,255,255,.72)", border:"1px solid rgba(212,175,55,.22)",
      backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
      borderRadius:14, overflow:"hidden", marginTop:4 }}>

      {/* Header */}
      <div style={{ padding:"20px 28px 16px", borderBottom:"1px solid rgba(212,175,55,.14)",
        display:"flex", justifyContent:"space-between", alignItems:"center",
        cursor:"pointer", userSelect:"none" }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:16, color: C.gold }}>{tLabel.icon}</span>
          <div>
            <div style={{ fontSize:11, letterSpacing:".2em", color:"rgba(212,175,55,.55)",
              textTransform:"uppercase", marginBottom:2 }}>
              {zh ? "个性化洞察" : "Personalized Insight"}
            </div>
            <div style={{ fontSize:15, color: C.gold, fontFamily:"'Inter',sans-serif", fontWeight:500 }}>
              {zh ? tLabel.zh : tLabel.en}
            </div>
          </div>
        </div>
        <div style={{ fontSize:12, color: C.mist3, transition:"transform 0.2s",
          transform: open ? "rotate(180deg)" : "none" }}>▾</div>
      </div>

      {/* Body */}
      {open && (
        <div style={{ padding:"20px 28px" }}>
          <ActiveView />

          {/* Practices footer */}
          {practices.length > 0 && (
            <>
              <GoldLine />
              <div style={{ fontSize:10.5, letterSpacing:".18em", color:"rgba(212,175,55,.5)",
                textTransform:"uppercase", marginBottom:10 }}>
                {zh ? "修行建议" : "Practices"}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {practices.map((p, i) => (
                  <div key={i} style={{ background:"rgba(212,175,55,.06)",
                    border:"1px solid rgba(212,175,55,.16)", borderRadius:8, padding:"10px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color: C.gold, marginBottom:3 }}>
                      {p.title}
                    </div>
                    <div style={{ fontSize:12.5, color: C.mist2, lineHeight:1.75 }}>
                      {p.body}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PersonalizedInsightPanel;
